/* ===========================================================================
   b2b-cart.js — companion for sections/b2b-cart.liquid (full /cart page).

   Drives the two dynamic sidebar widgets that can't be re-rendered by Dawn's
   cart.js (it only re-renders #main-cart-items / #main-cart-footer .js-contents):

     1) "Discount cascadat" — an ILLUSTRATIVE B2B discount simulator. It computes
        a cascaded discount from the cart's ex-VAT net + distinct category count,
        with two financial/logistic toggles. It is a sales/demo aid only and does
        NOT change the real checkout total (that stays Shopify-authoritative in
        the order summary).

     2) "Operations Review" — recomputes the real minimum-order check live.

   It reacts to PUB_SUB cartUpdate (published by cart.js / cart-drawer / quick-add)
   so the widgets stay in sync after quantity / remove / add. All built-in cart
   behaviour is untouched.
   =========================================================================== */
(function () {
  'use strict';

  var roundN = function (n, d) {
    return Number(n).toLocaleString('ro-RO', { minimumFractionDigits: d || 0, maximumFractionDigits: d || 0 });
  };
  var m = function (n) { return roundN(n, 0); };
  var pctTxt = function (p) { return p > 0 ? '−' + Math.round(p * 100) + '%' : '—'; };

  /* ---- cascade breakdown ---------------------------------------------------
     MUST stay a mirror of extensions/cascade-discount/src/cascade.ts. Both read
     the same shop metafield (b2b.cascade_config) and the same company segment
     (b2b.tier), so the panel can never disagree with the applied discount.
     Percentages are whole numbers (20 = 20%) and money is in bani, exactly as
     the function handles them.
     ------------------------------------------------------------------------ */
  function resolveSegment(raw, config) {
    var fallback = (config && config.default_segment) || 'standard';
    if (!raw) return fallback;
    var n = String(raw).toLowerCase().trim()
      .replace(/[ăâà]/g, 'a').replace(/[îí]/g, 'i')
      .replace(/[șş]/g, 's').replace(/[țţ]/g, 't');
    if (n.indexOf('distrib') === 0) return 'distribuitor';
    if (n.indexOf('prof') === 0) return 'professional';
    if (n.indexOf('stand') === 0) return 'standard';
    return fallback;
  }

  function toNet(subtotal, config) {
    var vat = config && config.vat;
    if (!vat || !vat.prices_include_vat) return subtotal;
    var rate = vat.rate || 0;
    return rate > 0 ? subtotal / (1 + rate / 100) : subtotal;
  }

  /* highest rule whose threshold is met wins */
  function bestPct(value, rules, key) {
    var pct = 0, best = -1;
    (rules || []).forEach(function (r) {
      if (value >= r[key] && r[key] > best) { best = r[key]; pct = r.pct || 0; }
    });
    return pct;
  }

  function Cascade(el) {
    this.el = el;
    this.readState();
    this.bind();
    this.render();
  }
  Cascade.prototype.readState = function () {
    var el = this.el;
    try { this.config = JSON.parse(el.dataset.config || '{}'); } catch (e) { this.config = {}; }
    this.subtotal = parseFloat(el.dataset.subtotal) || 0;   // bani, as Shopify reports it
    this.cats = parseInt(el.dataset.cats, 10) || 0;
    this.rawSegment = el.dataset.segment || '';
    this.paymentPref = el.dataset.paymentPref || '';
    this.deliveryPref = el.dataset.deliveryPref || '';
    this.rebate = el.dataset.rebateEligible === '1';
    this.toggleA = this.paymentPref === 'ramburs';
    // Pickup is DECLARED here, not detected at checkout: Shopify runs cart-line
    // discounts before delivery methods exist, so the function can only read this
    // attribute. Checkout validation blocks a buyer who declares pickup and then
    // picks shipping, so the cart and the checkout can't disagree.
    this.toggleB = this.deliveryPref === 'pickup';
    this.deliveryIsPickup = this.toggleB;
  };
  Cascade.prototype.rules = function (segment) {
    var segs = this.config.segments || {};
    return segs[segment] || { volume_tiers: [], assortment: [], modifiers: {} };
  };
  Cascade.prototype.compute = function () {
    var cfg = this.config;
    var seg = resolveSegment(this.rawSegment, cfg);
    var rules = this.rules(seg);
    var mods = rules.modifiers || {};
    var net = toNet(this.subtotal, cfg);

    var p1 = bestPct(net, rules.volume_tiers, 'min_net');
    var p2 = bestPct(this.cats, rules.assortment, 'min_categories');
    var p3 = 0;
    if (this.toggleA) p3 += mods.ramburs_pp || 0;
    if (this.deliveryIsPickup) p3 += mods.pickup_pp || 0;
    var pReb = this.rebate ? ((cfg.rebate && cfg.rebate.pct) || 0) : 0;

    var mul = function (p) { return 1 - p / 100; };
    var product = mul(p1) * mul(p2) * mul(p3) * mul(pReb);
    var minOrder = rules.min_order_net || 0;

    var belowMin = minOrder > 0 && net < minOrder;
    return {
      segment: seg, net: net, p1: p1, p2: p2, p3: p3, pReb: pReb,
      // round ONCE, at the end
      compound: Math.round(((1 - product) * 100 + Number.EPSILON) * 100) / 100,
      rambursPp: mods.ramburs_pp || 0,
      pickupPp: mods.pickup_pp || 0,
      belowMin: belowMin,
      minOrderRon: minOrder / 100,
      blocked: belowMin && !!rules.enforce_min_order
    };
  };
  /**
   * Persist a cart attribute and let the discount function recompute. The
   * function is the source of truth for the total — we never adjust it here.
   */
  Cascade.prototype.setAttribute = function (key, value) {
    var self = this;
    var attrs = {};
    attrs[key] = value;
    this.el.classList.add('is-busy');
    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('b2b:cart:refresh', { detail: cart }));
        return cart;
      })
      .catch(function (e) { console.error('[b2b-cascade] attribute update failed', e); })
      .finally(function () { self.el.classList.remove('is-busy'); });
  };
  Cascade.prototype.bind = function () {
    var self = this;
    // Both toggles are real cart attributes the discount function reads.
    this.el.querySelectorAll('[data-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.dataset.toggle === 'a') {
          self.toggleA = !self.toggleA;
          self.render();
          self.setAttribute('payment_pref', self.toggleA ? 'ramburs' : 'standard');
        } else if (btn.dataset.toggle === 'b') {
          self.toggleB = self.deliveryIsPickup = !self.toggleB;
          self.render();
          self.setAttribute('delivery_pref', self.toggleB ? 'pickup' : 'standard');
        }
      });
    });
    var reb = this.el.querySelector('[data-rebate-row]');
    if (reb) {
      var toggleReb = function () { self.rebate = !self.rebate; reb.classList.toggle('is-on', self.rebate); };
      reb.addEventListener('click', toggleReb);
      reb.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleReb(); } });
    }
  };
  /**
   * Re-read state from freshly rendered section markup. Category count comes
   * from a product metafield, which /cart.js does NOT expose — so the server
   * (Liquid) stays the source of truth and we re-read its output instead of
   * recomputing categories client-side.
   */
  Cascade.prototype.refreshFrom = function (el) {
    if (!el) return;
    ['config', 'subtotal', 'cats', 'segment', 'paymentPref', 'deliveryPref', 'rebateEligible'].forEach(function (k) {
      if (el.dataset[k] !== undefined) this.el.dataset[k] = el.dataset[k];
    }, this);
    this.readState();
    this.render();
  };
  Cascade.prototype.render = function () {
    var r = this.compute();
    var effStr = r.compound.toFixed(2).replace('.', ',') + '%';
    var netRon = r.net / 100;

    var setEff = this.el.querySelector('[data-role="eff"]');
    if (setEff) setEff.textContent = effStr;

    var finSub;
    if (r.p3 > 0) {
      // PAS 3 can carry either modifier or both — name whichever is actually on.
      var finParts = [];
      if (this.toggleA) finParts.push('ramburs −' + r.rambursPp + '%');
      if (this.toggleB) finParts.push('ridicare −' + r.pickupPp + '%');
      finSub = finParts.join(' · ');
    } else if (r.rambursPp === 0 && r.segment === 'distribuitor') {
      finSub = 'inclus în catalogul Distribuitor';
    } else {
      finSub = 'Net 30 · livrare standard';
    }

    var volSub = r.p1 > 0
      ? 'coș ' + m(netRon) + ' RON net'
      : (r.segment === 'distribuitor'
          ? 'inclus în prețul de catalog'
          : 'coș ' + m(netRon) + ' RON net');

    var steps = this.el.querySelectorAll('.b2b-cascade__step');
    var data = [
      { pct: r.p1, sub: volSub },
      { pct: r.p2, sub: this.cats + (this.cats === 1 ? ' categorie' : ' categorii') + ' distincte' },
      { pct: r.p3, sub: finSub }
    ];
    steps.forEach(function (step, i) {
      var d = data[i];
      if (!d) return;
      step.classList.toggle('is-active', d.pct > 0);
      var sub = step.querySelector('[data-role="sub"]');
      var pct = step.querySelector('[data-role="pct"]');
      if (sub) sub.textContent = d.sub;
      if (pct) pct.textContent = d.pct > 0 ? '−' + d.pct + '%' : '—';
    });

    var mul = function (p) { return (1 - p / 100).toFixed(2); };
    var formula = this.el.querySelector('[data-role="formula"]');
    if (formula) {
      var parts = [mul(r.p1), mul(r.p2), mul(r.p3)];
      if (r.pReb > 0) parts.push(mul(r.pReb));
      formula.innerHTML = '1−(' + parts.join('×') + ') = <strong>−' + effStr + '</strong>';
    }

    this.el.classList.toggle('is-below-min', !!r.belowMin);
    this.el.dataset.resolvedSegment = r.segment;

    // Hard minimum order: block checkout when the segment enforces one.
    var blocked = !!r.blocked;
    var btn = document.getElementById('checkout');
    if (btn) btn.disabled = blocked || btn.hasAttribute('data-cart-empty');
    var blocker = document.querySelector('[data-min-order-blocker]');
    if (blocker) blocker.hidden = !blocked;

    var ta = this.el.querySelector('[data-toggle="a"]');
    if (ta) ta.classList.toggle('is-on', this.toggleA);
    var tb = this.el.querySelector('[data-toggle="b"]');
    if (tb) tb.classList.toggle('is-on', this.toggleB);
  };

  /* ---- the real Operations Review (min-order check) ---------------------- */
  function updateOps(el, netRon) {
    var minOrder = parseFloat(el.dataset.minOrder) || 0;
    var minRow = el.querySelector('[data-check="min-order"]');
    if (minRow && minOrder > 0) {
      minRow.classList.toggle('is-bad', netRon < minOrder);
    }
    // recompute overall status from whatever rows are currently bad
    var anyBad = !!el.querySelector('.b2b-ops__check.is-bad');
    el.classList.toggle('is-warning', anyBad);
    var statusText = el.querySelector('[data-ops-status-text]');
    if (statusText) statusText.textContent = anyBad ? (el.dataset.warnText || '') : (el.dataset.okText || '');
  }

  /* ---- wire everything --------------------------------------------------- */
  function init() {
    var cascadeEl = document.querySelector('[data-b2b-cascade]');
    var opsEl = document.querySelector('[data-b2b-ops]');
    var root = document.querySelector('.b2b-cart');
    if (!cascadeEl && !opsEl && !root) return;

    var cascade = cascadeEl ? new Cascade(cascadeEl) : null;
    var vat = parseFloat((cascadeEl && cascadeEl.dataset.vat) || (opsEl && opsEl.dataset.vat) || 21);

    /**
     * Drift alarm: the widget explains the discount, the function applies it.
     * If they ever disagree, that is a bug worth shouting about — compare the
     * widget's compound against what Shopify actually took off the cart.
     */
    function checkDrift(cartData) {
      if (!cascade || !cartData || cartData.errors) return;
      if (!cartData.items_subtotal_price || typeof cartData.total_price !== 'number') return;
      var applied = (cartData.items_subtotal_price - cartData.total_price) /
                    cartData.items_subtotal_price * 100;
      var shown = cascade.compute().compound;
      // Every input the function uses now also lives in the cart, so the two
      // must agree exactly — any gap is a real bug.
      if (Math.abs(applied - shown) > 0.05) {
        console.error(
          '[b2b-cascade] DRIFT: widget shows ' + shown.toFixed(2) +
          '% but Shopify applied ' + applied.toFixed(2) + '%',
          { segment: cascade.el.dataset.resolvedSegment, cats: cascade.cats }
        );
      }
    }

    /**
     * Pull freshly rendered section markup so the widget re-reads server-computed
     * values (category count needs a product metafield that /cart.js can't give us).
     */
    function refreshFromSection() {
      var sectionId = (cascadeEl && cascadeEl.dataset.sectionId) || (opsEl && opsEl.dataset.sectionId) || '';
      if (!sectionId) return Promise.resolve();
      return fetch(window.location.pathname + '?section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          // Order summary. Toggling ramburs / ridicare changes the discount, so
          // every figure in this card moves — but Dawn's cart.js only re-renders
          // it on quantity edits, which an attribute update never triggers. Left
          // alone, the card shows stale money until the buyer reloads. Only
          // .js-contents is swapped: the checkout button, the min-order blocker
          // and the PO textarea live outside it and must keep their state.
          var summaryEl = document.querySelector('#main-cart-footer .js-contents');
          var freshSummary = doc.querySelector('#main-cart-footer .js-contents');
          if (summaryEl && freshSummary) summaryEl.innerHTML = freshSummary.innerHTML;

          if (cascade) cascade.refreshFrom(doc.querySelector('[data-b2b-cascade]'));
          // Operations Review is server-computed (the stock check needs inventory
          // data /cart.js doesn't expose, and the minimum is segment-derived), so
          // take the freshly rendered card wholesale rather than recompute it.
          var freshOps = doc.querySelector('[data-b2b-ops]');
          if (opsEl && freshOps) {
            opsEl.className = freshOps.className;
            opsEl.innerHTML = freshOps.innerHTML;
            ['minOrder', 'minEnforced', 'segment'].forEach(function (k) {
              if (freshOps.dataset[k] !== undefined) opsEl.dataset[k] = freshOps.dataset[k];
            });
          }
        })
        .catch(function (e) { console.error('[b2b-cascade] section refresh failed', e); });
    }

    /**
     * Surface a cart error (stock limits, validation, anything Shopify rejects)
     * in the order summary, where it's always visible — the per-line message
     * cart.js writes can be scrolled out of view.
     */
    function showCartError(raw) {
      var box = document.getElementById('cart-errors');
      if (!box) return;
      var msg = raw;
      if (typeof msg !== 'string' || !msg) msg = (window.cartStrings && window.cartStrings.error) || 'Coșul nu a putut fi actualizat.';
      if (window.kldRoCartMessage) msg = window.kldRoCartMessage(msg);
      box.textContent = msg;
      box.classList.add('is-visible');
    }
    function clearCartError() {
      var box = document.getElementById('cart-errors');
      if (!box) return;
      box.textContent = '';
      box.classList.remove('is-visible');
    }

    /**
     * A REJECTED cart operation still publishes cartUpdate, but the payload is
     * the error body ({errors: "..."}) — no item_count, no items. Treating that
     * as cart state marks the cart empty and blanks the page, hiding the very
     * message the buyer needs. Show the error and leave the cart alone.
     */
    function isErrorPayload(cartData) {
      return !!cartData.errors || typeof cartData.item_count === 'undefined';
    }

    function recompute(cartData) {
      if (!cartData) return;
      if (isErrorPayload(cartData)) {
        showCartError(cartData.errors);
        return;
      }
      clearCartError();
      var netRon = ((cartData.items_subtotal_price || 0) / 100) * 100 / (100 + vat); // ex-VAT RON
      if (opsEl) updateOps(opsEl, netRon);
      if (root) root.classList.toggle('is-cart-empty', cartData.item_count === 0);
      // categories + segment come from the server, not from cart.js
      refreshFromSection().then(function () { checkDrift(cartData); });
    }

    document.addEventListener('b2b:cart:refresh', function () { refreshFromSection(); });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.cartData) {
          recompute(event.cartData);
        } else {
          fetch((((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')) + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(recompute)
            .catch(function () {});
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
